import React from 'react';

import {
  Alert,
  Button,
  Center,
  Group,
  LoadingOverlay,
  Modal,
  NumberInput,
  SimpleGrid,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { DateInput, TimeInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useShallowEffect } from '@mantine/hooks';
import { IconExclamationCircle } from '@tabler/icons-react';
import OtherWorkTypeAutoComplete, {
  OtherWorkTypeAutoCompleteValue,
} from 'components/form-fields/auto-complete-fields/OtherWorkTypeAutoComplete';
import dayjs, { Dayjs } from 'dayjs';
import useOtherWorkEvent from 'hooks/otherWorkEvent/useOtherWorkEvent';
import useUpdateOtherWorkEvent from 'hooks/otherWorkEvent/useUpdateOtherWorkEvent';
import { PayrollEventRetrieve } from 'types';
import { calculateEventEndDateTime } from 'utils/event.helpers';
import notificationHandler from 'utils/notifications';

interface IModalOtherWorkEventEditProps {
  opened: boolean;
  handleClose: () => void;
  payrollEvent?: PayrollEventRetrieve;
}

interface FormValues {
  otherWorkEventId: string;
  otherWorkType: OtherWorkTypeAutoCompleteValue;
  date: Date;
  time: string;
  duration: number;
  dayDiff: string;
  otherWorkEventEnd: Dayjs | null;
}

const ModalOtherWorkEventEdit = ({
  opened,
  handleClose,
  payrollEvent,
}: IModalOtherWorkEventEditProps): JSX.Element => {
  const updateMutation = useUpdateOtherWorkEvent();
  const { data, isLoading, isSuccess, isError } = useOtherWorkEvent(payrollEvent?.genesisEventId);
  const theme = useMantineTheme();

  const form = useForm<FormValues>({
    initialValues: {
      otherWorkEventId: '',
      date: dayjs().toDate(),
      time: dayjs().format('HH:mm').toString(),
      duration: 0,
      otherWorkType: {
        otherWorkTypeId: '',
        value: '',
        label: '',
      },
      dayDiff: '',
      otherWorkEventEnd: null,
    },
    validate: values => ({
      otherWorkType: values.otherWorkType ? null : 'Other work type is required',
      date: values.date ? null : 'Date is required',
      time: values.time ? null : 'Time is required',
      duration: values.duration ? null : 'Duration is required',
    }),
  });

  const handleOtherWorkTypeChange = (item: OtherWorkTypeAutoCompleteValue | null): void => {
    const selectedOtherWorkType = {
      otherWorkTypeId: item ? item.otherWorkTypeId : '',
      value: item ? item.value : '',
      label: item ? item.label : '',
    };
    form.setFieldValue('otherWorkType', selectedOtherWorkType);
  };

  const handleCloseAndReset = (): void => {
    handleClose();
    form.reset();
  };

  const { successNotification, errorNotification } = notificationHandler();
  const handleSubmit = (values: FormValues): void => {
    if (payrollEvent) {
      updateMutation.mutate(
        {
          ...values,
          staff: payrollEvent.staffId,
          otherWorkType: values.otherWorkType.otherWorkTypeId,
          date: dayjs(values.date).format('YYYY-MM-DD'),
        },
        {
          onSuccess: () => {
            successNotification(
              'Updating other work event success',
              `${values.otherWorkType.label} has been successfully updated.`,
            );
            handleCloseAndReset();
          },
          onError: error => {
            errorNotification('Update other work event failed', error.response?.data.message);
          },
        },
      );
    }
  };

  useShallowEffect(() => {
    if (form.values.duration && form.values.time) {
      const otherWorkEventEnd = calculateEventEndDateTime(
        form.values.duration,
        form.values.time,
        form.values.date,
      );
      form.setFieldValue('otherWorkEventEnd', otherWorkEventEnd);
      const start = dayjs(form.values.date);
      const diff = otherWorkEventEnd.date() - start.date();
      let dayDiff = '';
      if (diff === 1) {
        dayDiff = '+1 day';
      }
      if (diff > 1) {
        dayDiff = `+${diff} days`;
      }
      form.setFieldValue('dayDiff', dayDiff);
    } else {
      form.setFieldValue('otherWorkEventEnd', null);
      form.setFieldValue('dayDiff', '');
    }
  }, [form.values.duration, form.values.time, form.values.date]);

  useShallowEffect(() => {
    if (data?.data && isSuccess) {
      form.setValues({
        ...data.data,
        otherWorkType: {
          otherWorkTypeId: data.data.otherWorkType.otherWorkTypeId,
          value: data.data.otherWorkType.otherWorkTypeId,
          label: data.data.otherWorkType.name,
        },
        date: dayjs(data.data.date).toDate(),
      });
    }
  }, [data?.data]);

  if (isError) {
    return (
      <Center sx={{ flexGrow: 1 }} p='lg'>
        <Alert icon={<IconExclamationCircle size={theme.fontSizes.md} />} title='Error' color='red'>
          Something went wrong while fetching data! Please try regenerating reports.
        </Alert>
      </Center>
    );
  }

  return (
    <Modal
      centered
      opened={opened}
      onClose={handleCloseAndReset}
      title={<Title order={4}>Edit Other Work Event</Title>}
      size='auto'
      withinPortal
    >
      <LoadingOverlay visible={isLoading} />
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <OtherWorkTypeAutoComplete
            label='Other Work Type'
            placeholder='Select an other work type'
            value={form.values.otherWorkType}
            error={form.errors.otherWorkType}
            onChange={handleOtherWorkTypeChange}
            required
          />
          <Group grow align='top'>
            <NumberInput
              {...form.getInputProps('duration')}
              label='Duration in minutes'
              placeholder='Enter duration'
              required
              data-cy='otherWorkEventDuration'
              min={15}
              max={60 * 24 * 7}
            />
            <div />
          </Group>
          <SimpleGrid cols={2} breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
            <DateInput
              {...form.getInputProps('date')}
              label='Date'
              locale='us'
              required
              popoverProps={{ zIndex: 10000000, withinPortal: true }}
            />
            <Group spacing='xs' grow>
              <TimeInput label='From' {...form.getInputProps('time')} required />
              <TimeInput
                label={
                  <Group position='apart'>
                    <div>To</div>
                    <Text color='red'>{form.values.dayDiff}</Text>
                  </Group>
                }
                value={form.values.otherWorkEventEnd ? form.values.otherWorkEventEnd.format('HH:mm') : ''}
                readOnly
                disabled
              />
            </Group>
          </SimpleGrid>
          <Group position='right'>
            <Button variant='subtle' onClick={handleCloseAndReset} disabled={updateMutation.isLoading}>
              Cancel
            </Button>
            <Button
              disabled={updateMutation.isLoading || isLoading}
              type='submit'
              data-cy='otherWorkEventSaveButton'
            >
              Save
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default ModalOtherWorkEventEdit;
